import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { ModalPopup } from "../../common";
import { UsageDistributionSegment } from "../../../types/analytics/UsageDistributionSegment";
import {
  COMPLETED_KEY,
  NOT_STARTED_KEY,
  WAITING_FOR_EMPLOYEE_KEY,
  WAITING_FOR_MANAGER_KEY,
} from "../../../helpers/usage/usageStatusChartHelpers";

interface UsageStatusLegendExplainerProps {
  segments: UsageDistributionSegment[];
}

/**
 * Short-form version of the widget help text. The lines are driven off the same
 * segments as the bar so the explainer can never describe a status the reader
 * is not looking at.
 */
function UsageStatusLegendExplainer({
  segments,
}: UsageStatusLegendExplainerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const explanations: Record<string, string> = {
    [NOT_STARTED_KEY]: t(
      "Analytics.Widgets.Usage.Band.Explainer.NotStarted",
      "Assigned the journey but they have not opened it yet.",
    ),
    [WAITING_FOR_EMPLOYEE_KEY]: t(
      "Analytics.Widgets.Usage.Band.Explainer.WaitingForEmployee",
      "Waiting on the employee's next step, including anyone who has started but not submitted.",
    ),
    [WAITING_FOR_MANAGER_KEY]: t(
      "Analytics.Widgets.Usage.Band.Explainer.WaitingForManager",
      "Waiting on the manager's next step, such as reviewing or approving.",
    ),
    [COMPLETED_KEY]: t(
      "Analytics.Widgets.Usage.Band.Explainer.Completed",
      "Everyone involved has finished the journey.",
    ),
  };

  const explainedSegments = segments.filter(
    (segment) => explanations[segment.id],
  );

  if (explainedSegments.length === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
        onClick={() => setIsOpen(true)}
      >
        <FontAwesomeIcon icon={faCircleQuestion} aria-hidden />
        {t(
          "Analytics.Widgets.Usage.Band.Explainer.Trigger",
          "What do these mean?",
        )}
      </button>

      <ModalPopup
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        width="TINY"
        title={t(
          "Analytics.Widgets.Usage.Band.Explainer.Title",
          "What each status means",
        )}
      >
        <ul className="space-y-3 py-1">
          {explainedSegments.map((segment) => (
            <li key={segment.id} className="flex gap-2.5">
              <span
                className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
                aria-hidden
              />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">
                  {segment.displayLabel}
                </span>
                <span className="block text-gray-600">
                  {explanations[segment.id]}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </ModalPopup>
    </>
  );
}

export default UsageStatusLegendExplainer;
